/**
 * Start Administration Modal
 * 
 * Modal for initiating the first step of two-step medication administration.
 * Includes vital signs collection, timing validation, and nurse identification.
 */

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Alert,
    Space,
    Button,
    Divider,
    Row,
    Col,
    Card,
    Select,
    DatePicker,
    message
} from 'antd';
import {
    UserOutlined,
    MedicineBoxOutlined,
    ExclamationCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import {
    validateAdministrationWindow
} from '../../../utils/administrationValidation';
import { getCurrentUserId, getCurrentUserName } from '../../../utils/authUtils';

const { TextArea } = Input;
const { Option } = Select;

const StartAdministrationModal = ({
    open,
    session,
    patient,
    medication,
    onStart,
    onCancel,
    zIndex = 15000 // Very high z-index to ensure modal appears above everything
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [windowValidation, setWindowValidation] = useState(null);
    const [vitals, setVitals] = useState([]);
    const [administrationTime, setAdministrationTime] = useState(null);
    // Start modal no longer handles justification. This is collected in End Administration only.

    // Vital signs options
    const vitalTypes = [
        { value: 'blood_pressure', label: 'Blood Pressure', unit: 'mmHg' },
        { value: 'heart_rate', label: 'Heart Rate', unit: 'bpm' },
        { value: 'respiratory_rate', label: 'Respiratory Rate', unit: '/min' },
        { value: 'temperature', label: 'Temperature', unit: '°F' },
        { value: 'oxygen_saturation', label: 'Oxygen Saturation', unit: '%' },
        { value: 'blood_glucose', label: 'Blood Glucose', unit: 'mg/dL' },
        { value: 'pain_scale', label: 'Pain Scale', unit: '0-10' }
    ];

    useEffect(() => {
        if (open && session) {
            // Initialize form with current user and current date and time
            const defaultTime = dayjs();
            setAdministrationTime(defaultTime);
            form.setFieldsValue({
                nurse_id: getCurrentUserId(),
                nurse_name: getCurrentUserName(),
                administration_time: defaultTime
            });

            // Validate administration window
            if (session.scheduled_time) {
                const validation = validateAdministrationWindow(session.scheduled_time);
                setWindowValidation(validation);
            }

            // Preload required pre-administration vitals from workflow/medication
            const requiredPreVitals = Array.isArray(medication?.pre_administration_vitals)
                ? medication.pre_administration_vitals
                : [];

            if (requiredPreVitals.length > 0) {
                setVitals(requiredPreVitals.map((vt) => {
                    const unit = vitalTypes.find(v => v.value === vt)?.unit || '';
                    return {
                        id: Date.now() + Math.random(),
                        type: vt,
                        value: '',
                        unit,
                        notes: ''
                    };
                }));
            } else {
                // Clear vitals if none required
                setVitals([]);
            }
        }
    }, [open, session, form]);

    // No justification logic needed in Start modal

    const addVital = () => {
        setVitals([...vitals, {
            id: Date.now(),
            type: '',
            value: '',
            unit: '',
            notes: ''
        }]);
    };

    const removeVital = (id) => {
        setVitals(vitals.filter(v => v.id !== id));
    };

    const updateVital = (id, field, value) => {
        setVitals(vitals.map(v =>
            v.id === id
                ? {
                    ...v,
                    [field]: value,
                    // Auto-update unit when type changes
                    ...(field === 'type' ? {
                        unit: vitalTypes.find(vt => vt.value === value)?.unit || ''
                    } : {})
                }
                : v
        ));
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Prepare vitals data
            const validVitals = vitals
                .filter(v => v.type && v.value)
                .map(v => ({
                    type: v.type,
                    value: v.value,
                    unit: v.unit || vitalTypes.find(vt => vt.value === v.type)?.unit || '',
                    measured_at: dayjs().toISOString(),
                    measured_by: values.nurse_id,
                    notes: v.notes || null
                }));

            const startData = {
                nurse_id: values.nurse_id,
                nurse_name: values.nurse_name,
                notes: values.notes || null,
                vitals: validVitals,
                start_time: values.administration_time ? dayjs(values.administration_time).toISOString() : dayjs().toISOString(),
                // No justification collected at start
            };

            console.log('Start Administration - Form values:', values);
            console.log('Start Administration - Selected time:', values.administration_time?.format('YYYY-MM-DD HH:mm:ss'));
            console.log('Start Administration - ISO time being sent as start_time:', startData.start_time);
            console.log('Start Administration - Full payload:', startData);

            await onStart(startData);
            form.resetFields();
            setVitals([]);
        } catch (error) {
            console.error('Form validation error:', error);
            const status = error?.response?.status;
            if (status === 403) {
                message.error(error?.response?.data?.detail || 'You do not have permission to start administration.');
            }
        } finally {
            setLoading(false);
        }
    };

    const renderWindowValidation = () => {
        if (!windowValidation) return null;

        const alertType = windowValidation.isValid ?
            (windowValidation.severity === 'success' ? 'success' : 'info') : 'error';

        return (
            <Alert
                message="Administration Window Validation"
                description={windowValidation.message}
                type={alertType}
                showIcon
                style={{ marginBottom: 16 }}
                icon={windowValidation.isValid ? undefined : <ExclamationCircleOutlined />}
            />
        );
    };

    const renderVitalsSection = () => (
        <Card size="small" title="Pre-Administration Vitals" style={{ marginBottom: 16 }}>
            {/* <div style={{ marginBottom: 12 }}>
                <Button onClick={addVital} size="small">
                    Add Vital Sign
                </Button>
            </div> */}

            {vitals.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
                    No vital signs added. Click "Add Vital Sign" to record pre-administration vitals.
                </div>
            ) : (
                vitals.map((vital) => (
                    <Card key={vital.id} size="small" style={{ marginBottom: 8 }}>
                        <Row gutter={[8, 8]} align="middle">
                            <Col xs={24} sm={6}>
                                <Select
                                    placeholder="Select vital"
                                    getPopupContainer={(trigger) => trigger?.closest('.ant-modal') || document.body}
                                    dropdownMatchSelectWidth={false}
                                    dropdownStyle={{ zIndex: 16000, pointerEvents: 'auto' }}
                                    value={vital.type}
                                    onChange={(value) => updateVital(vital.id, 'type', value)}
                                    size="small"
                                    style={{ width: '100%' }}
                                >
                                    {vitalTypes.map(vt => (
                                        <Option key={vt.value} value={vt.value}>
                                            {vt.label}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                            <Col xs={12} sm={4}>
                                <Input
                                    placeholder="Value"
                                    value={vital.value}
                                    onChange={(e) => updateVital(vital.id, 'value', e.target.value)}
                                    size="small"
                                />
                            </Col>
                            <Col xs={12} sm={3}>
                                <Input
                                    placeholder="Unit"
                                    value={vital.unit}
                                    onChange={(e) => updateVital(vital.id, 'unit', e.target.value)}
                                    size="small"
                                />
                            </Col>
                            <Col xs={20} sm={8}>
                                <Input
                                    placeholder="Notes (optional)"
                                    value={vital.notes}
                                    onChange={(e) => updateVital(vital.id, 'notes', e.target.value)}
                                    size="small"
                                />
                            </Col>
                            <Col xs={4} sm={3}>
                                <Button
                                    danger
                                    size="small"
                                    onClick={() => removeVital(vital.id)}
                                >
                                    Remove
                                </Button>
                            </Col>
                        </Row>
                    </Card>
                ))
            )}
        </Card>
    );

    if (!session) return null;

    const canProceed = !windowValidation || windowValidation.isValid;

    // Debug logging
    console.log('StartAdministrationModal render:', { open, session, patient, medication, zIndex });

    return (
        <>
            {/* Force high z-index for administration modals */}
            <style>
                {`
                .administration-modal-wrapper .ant-modal-wrap {
                    z-index: 15000 !important;
                    position: fixed !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                }
                .administration-modal-wrapper .ant-modal-mask {
                    z-index: 14999 !important;
                    position: fixed !important;
                    background: rgba(0, 0, 0, 0.7) !important;
                }
                .administration-modal-wrapper .ant-modal {
                    z-index: 15000 !important;
                    position: relative !important;
                    margin: 0 auto !important;
                    top: auto !important;
                    left: auto !important;
                    transform: none !important;
                }
                .administration-modal-wrapper {
                    z-index: 15000 !important;
                    position: fixed !important;
                }
                .administration-modal-wrapper .ant-modal-content {
                    pointer-events: auto !important;
                }
                .administration-modal-wrapper .ant-input,
                .administration-modal-wrapper .ant-select,
                .administration-modal-wrapper .ant-form-item {
                    pointer-events: auto !important;
                }
                `}
            </style>
            <Modal
            title={
                <Space>
                    <UserOutlined />
                    Start Administration - Step 1 of 2
                </Space>
            }
            open={open}
            onCancel={onCancel}
            width={800}
            zIndex={zIndex}
            mask={true}
            maskClosable={false}
            getContainer={() => document.body}
            style={{ zIndex: zIndex }}
            styles={{ mask: { zIndex: 14999 } }}
            wrapClassName="administration-modal-wrapper"
            destroyOnHidden={false}
            forceRender={true}
            focusTriggerAfterClose={false}
            footer={[
                <Button key="cancel" onClick={onCancel}>
                    Cancel
                </Button>,
                <Button
                    key="start"
                    type="primary"
                    loading={loading}
                    disabled={!canProceed}
                    onClick={handleSubmit}
                >
                    Start Administration
                </Button>
            ]}
        >
            <Alert
                message="Starting Two-Step Administration"
                description={`Starting administration for ${medication?.medication_name || medication?.brand_name}. This will begin the administration process and allow a different nurse to complete it.`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
            />

            {renderWindowValidation()}

            <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                    <Card size="small" title="Patient Information">
                        <div><strong>Name:</strong> {patient?.full_name}</div>
                        <div><strong>MRN:</strong> {patient?.mrn}</div>
                        <div><strong>Age:</strong> {patient?.age} years</div>
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card size="small" title="Medication Information">
                        <div><strong>Medication:</strong> {medication?.medication_name || medication?.brand_name}</div>
                        <div><strong>Route:</strong> {medication?.route || medication?.route_of_administration}</div>
                        <div><strong>Scheduled:</strong> {session.scheduled_time ? dayjs(session.scheduled_time).format('hh:mm A') : 'Not specified'}</div>
                    </Card>
                </Col>
            </Row>

            <Divider />

            <Form form={form} layout="vertical">
                <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Starting Nurse ID"
                            name="nurse_id"
                            rules={[{ required: true, message: 'Nurse ID is required' }]}
                        >
                            <Input
                                prefix={<UserOutlined />}
                                placeholder="Enter your nurse ID"
                                readOnly
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Starting Nurse Name"
                            name="nurse_name"
                            rules={[{ required: true, message: 'Nurse name is required' }]}
                        >
                            <Input
                                placeholder="Enter your full name"
                                readOnly
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 0]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Administration Date & Time"
                            name="administration_time"
                            rules={[{ required: true, message: 'Administration time is required' }]}
                        >
                            <div style={{ width: '100%' }}>
                                <DatePicker
                                    showTime
                                    format="YYYY-MM-DD HH:mm:ss"
                                    placeholder="Select administration time"
                                    style={{ width: '100%', marginBottom: '8px' }}
                                    getPopupContainer={(trigger) => trigger?.closest('.ant-modal') || document.body}
                                    popupStyle={{ zIndex: 16000, pointerEvents: 'auto' }}
                                    value={administrationTime}
                                    onChange={(value) => {
                                        setAdministrationTime(value);
                                        form.setFieldsValue({ administration_time: value });
                                    }}
                                    disabledDate={(current) => {
                                        // Disable dates that are more than 24 hours old OR future dates
                                        const now = dayjs();
                                        const twentyFourHoursAgo = now.subtract(24, 'hours');
                                        return current && (current.isAfter(now, 'day') || current.isBefore(twentyFourHoursAgo, 'day'));
                                    }}
                                    disabledTime={(current) => {
                                        if (!current) return {};

                                        const now = dayjs();
                                        const twentyFourHoursAgo = now.subtract(24, 'hours');
                                        const currentDate = dayjs(current);

                                        // Handle today's date - disable times after current time
                                        if (currentDate.isSame(now, 'day')) {
                                            return {
                                                disabledHours: () => {
                                                    const hours = [];
                                                    for (let i = now.hour() + 1; i <= 23; i++) {
                                                        hours.push(i);
                                                    }
                                                    return hours;
                                                },
                                                disabledMinutes: (selectedHour) => {
                                                    if (selectedHour === now.hour()) {
                                                        const minutes = [];
                                                        for (let i = now.minute() + 1; i <= 59; i++) {
                                                            minutes.push(i);
                                                        }
                                                        return minutes;
                                                    }
                                                    return [];
                                                },
                                                disabledSeconds: (selectedHour, selectedMinute) => {
                                                    if (selectedHour === now.hour() && selectedMinute === now.minute()) {
                                                        const seconds = [];
                                                        for (let i = now.second() + 1; i <= 59; i++) {
                                                            seconds.push(i);
                                                        }
                                                        return seconds;
                                                    }
                                                    return [];
                                                }
                                            };
                                        }

                                        // Handle the date 24 hours ago - disable times before the cutoff
                                        if (currentDate.isSame(twentyFourHoursAgo, 'day')) {
                                            return {
                                                disabledHours: () => {
                                                    const hours = [];
                                                    for (let i = 0; i < twentyFourHoursAgo.hour(); i++) {
                                                        hours.push(i);
                                                    }
                                                    return hours;
                                                },
                                                disabledMinutes: (selectedHour) => {
                                                    if (selectedHour === twentyFourHoursAgo.hour()) {
                                                        const minutes = [];
                                                        for (let i = 0; i < twentyFourHoursAgo.minute(); i++) {
                                                            minutes.push(i);
                                                        }
                                                        return minutes;
                                                    }
                                                    return [];
                                                },
                                                disabledSeconds: (selectedHour, selectedMinute) => {
                                                    if (selectedHour === twentyFourHoursAgo.hour() && selectedMinute === twentyFourHoursAgo.minute()) {
                                                        const seconds = [];
                                                        for (let i = 0; i < twentyFourHoursAgo.second(); i++) {
                                                            seconds.push(i);
                                                        }
                                                        return seconds;
                                                    }
                                                    return [];
                                                }
                                            };
                                        }

                                        return {};
                                    }}
                                    showNow={false}
                                />
                                <Button
                                    size="small"
                                    type="link"
                                    onClick={() => {
                                        const now = dayjs();
                                        setAdministrationTime(now);
                                        form.setFieldsValue({
                                            administration_time: now
                                        });
                                        form.validateFields(['administration_time']);
                                    }}
                                    style={{ marginTop: '4px', padding: '0', height: 'auto' }}
                                >
                                    Set to Now
                                </Button>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Justification removed from Start step. It is collected in End Administration only. */}

                <Form.Item
                    label="Start Notes"
                    name="notes"
                >
                    <TextArea
                        rows={3}
                        placeholder="Enter any notes about starting this administration (optional)"
                        maxLength={500}
                        showCount
                    />
                </Form.Item>
            </Form>

            {renderVitalsSection()}

            {!canProceed && (
                <Alert
                    message="Cannot Start Administration"
                    description="Administration cannot be started outside the 24-hour window from scheduled time."
                    type="error"
                    showIcon
                    style={{ marginTop: 16 }}
                />
            )}
        </Modal>
        </>
    );
};

export default StartAdministrationModal;